import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    useFieldArray,
} from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_SURV_QUES_INFO,
    SF_SURV_ITM_INFO,
    SG_SURV_ITM_LANG_LIST,
} from "./services/PtliSurvQuesMgmtService";

/*
 * Survey Question Detail
 * !== 설문 질문 상세 ==!
 */

export const PTLI0419004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { qestnrId, quesId } = useParams();

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survQuesInfo: useForm({
            defaultSchema: SF_SURV_QUES_INFO,
        }),
        survItmInfo: useForm({
            defaultSchema: SF_SURV_ITM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survItmLangList: useGrid({
            defaultSchema: SG_SURV_ITM_LANG_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */

    const { fields: itemFields, remove, append, prepend, insert } = useFieldArray(form.survItmInfo.schema.items);

    const fetch = {
        getSurvQuesInfo: useFetch({
            api: () => APIS.getSurvQuesInfo(qestnrId, quesId),
            enabled: !!quesId,
            onSuccess: (data) => {
                const { content } = data.survQuesInfo || {};
                if (comnUtils.isEmpty(content)) {
                    modal.openModal({ content: "msg.ptl.00694" }); // Abnormal access., !== 비정상 접근입니다. ==!
                    navigate(URLS.ptli0419001q);
                    return;
                }

                // 1) 기본 필드 세팅 (qestnrId, quesId, quesTpCd, quesOrd 등)
                form.survQuesInfo.setValues(content);

                const { survQuesLangList } = content;

                if (!comnUtils.isEmpty(survQuesLangList)) {
                    survQuesLangList.forEach((row: any) => {
                        const { langCd, quesCn } = row;
                        if (!comnUtils.isEmpty(langCd)) {
                            form.survQuesInfo.setValue(`quesCn${langCd}`, quesCn ?? "");
                        }
                    });
                }
                if (String(content.quesTpCd) === "1") {
                    const serverItems: any[] = content.survItmInfo ?? [];

                    // 기존 필드배열 비우고 재구성
                    const curr = form.survItmInfo.getValue("items") || [];
                    for (let i = curr.length - 1; i >= 0; i--) remove(i);

                    // 서버 -> 폼 스키마 형태로 변환 후 append
                    serverItems
                        .sort((a, b) => Number(a?.itmOrd ?? 0) - Number(b?.itmOrd ?? 0))
                        .forEach((itm) => {
                            const row: any = { itmOrd: String(itm?.itmOrd ?? "") };
                            (itm?.survItmLangList ?? []).forEach((itmList: any) => {
                                if (!comnUtils.isEmpty(itmList?.langCd)) {
                                    row[`itmCn${itmList.langCd}`] = itmList?.itmCn ?? "";
                                }
                            });
                            append(row);
                        });

                    // (옵션) 숨김 그리드 동기화
                    grid.survItmLangList.clearData();
                    serverItems.forEach((itm) => {
                        (itm?.survItmLangList ?? []).forEach((itmList: any) => {
                            if (!comnUtils.isEmpty(itmList?.itmCn)) {
                                grid.survItmLangList.addRow({ langCd: itmList.langCd, itmCn: itmList.itmCn });
                            }
                        });
                    });
                } else {
                    // 주관식이면 아이템/그리드 비움
                    const curr = form.survItmInfo.getValue("items") || [];
                    for (let i = curr.length - 1; i >= 0; i--) remove(i);
                    grid.survItmLangList.clearData();
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteSurvQuesInfo: useFetch({
            api: (data) => APIS.deleteSurvQuesInfo(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(URLS.ptli0419001q);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        deleteSurvQuesInfo: () => {
            modal.openModal({
                content: "msg.00103", // Do you want to delete ? !== 삭제 하시겠습니까? ==!
                onConfirm: () => {
                    const data = form.survQuesInfo.getValues();

                    fetch.deleteSurvQuesInfo.fetch(data);
                },
            });
        },
    };

    const currQuesTpCd = form.survQuesInfo.watch("quesTpCd");

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvQuesInfo.fetch(qestnrId, quesId);
        form.survQuesInfo.setEditable(false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_QUESTIONS")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.qustnrTtle}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesOrd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesCnEN}></Group.Control>
                                <Group.Control {...form.survQuesInfo.schema.quesCnTZ}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.survQuesInfo.schema.quesCnKO}></Group.Control>
                            </Group.Row>
                            {currQuesTpCd === "1" && (
                                <Group.Row>
                                    <Group.Control {...form.survQuesInfo.schema.maxSeltCnt}></Group.Control>
                                </Group.Row>
                            )}
                        </Group.Section>
                    </Group.Body>
                </Group>
                {currQuesTpCd === "1" && (
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_ITEMS")} />
                        </Group.Header>
                        <Group.Body>
                            {itemFields.length === 0 && (
                                <Group.Section>
                                    <Group.Row>{t("No data")}</Group.Row>
                                </Group.Section>
                            )}

                            {itemFields.map((fx, i) => {
                                fx.schema.itmOrd.readOnly = true;
                                fx.schema.itmCnEN.readOnly = true;
                                fx.schema.itmCnTZ.readOnly = true;
                                fx.schema.itmCnKO.readOnly = true;

                                return (
                                    <Group.Section key={fx.key}>
                                        <Group.Row>
                                            <Group.Control {...fx.schema.itmOrd} />
                                        </Group.Row>
                                        <Group.Row>
                                            <Group.Control {...fx.schema.itmCnEN} />
                                            <Group.Control {...fx.schema.itmCnTZ} />
                                        </Group.Row>
                                        <Group.Row>
                                            <Group.Control {...fx.schema.itmCnKO} />
                                        </Group.Row>
                                    </Group.Section>
                                );
                            })}
                        </Group.Body>
                    </Group>
                )}
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0419001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.deleteSurvQuesInfo();
                        }}
                    ></Button>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0419003s}/${qestnrId}/${quesId}`);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
